import {
  Button,
  Checkbox,
  Input,
  mergeClasses,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Spinner,
  Tab,
  TabList,
  Text,
  Textarea,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  ArrowClockwise24Regular,
  bundleIcon,
  ChevronDown16Regular,
  ChevronRight16Regular,
  Filter24Regular,
  Search24Filled,
  Search24Regular,
} from '@fluentui/react-icons';
import { useEffect, useRef, useState } from 'react';

import type { LoadJsonMode } from '../../../hooks/useAgentDebugData/useAgentDebugData';
import type { OverviewEntry } from '../../../types/debug';

import type { OverviewViewProps } from './OverviewView.interface';
import { useStyles } from './OverviewView.style';

const FetchIcon = bundleIcon(Search24Filled, Search24Regular) as React.FC;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildCalendarDays<T>(
  entries: T[],
  getTimestamp: (entry: T) => string,
  days: number,
): { label: string; dayStart: number; entries: T[] }[] {
  const now = new Date();
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();

  return Array.from({ length: days }, (_, i) => {
    const dayStart = todayStart - (days - 1 - i) * 86400000;
    const dayEnd = dayStart + 86400000;
    let label: string;
    if (dayStart === todayStart) {
      label = 'Today';
    } else if (dayStart === todayStart - 86400000) {
      label = 'Yesterday';
    } else {
      label = new Date(dayStart).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    }
    return {
      label,
      dayStart,
      entries: entries.filter((e) => {
        const t = new Date(getTimestamp(e)).getTime();
        return t >= dayStart && t < dayEnd;
      }),
    };
  });
}

function debugHref(identifier: string) {
  return '/debug?cv=' + encodeURIComponent(identifier);
}

function getSignalIdentifier(entry: OverviewEntry): string {
  if (entry.signalId) return entry.signalId;
  if (entry.cv) return entry.cv;
  return entry.itemId;
}

function abbreviateIdentifier(identifier: string): string {
  if (identifier.length <= 16) return identifier;
  return `${identifier.slice(0, 7)}…${identifier.slice(-6)}`;
}

function getEntryKey(entry: OverviewEntry): string {
  return entry.signalId
    ? entry.signalId
    : `${entry.itemId}\u0000${entry.cv}\u0000${entry.startTime}`;
}

interface CalendarEntryGroup {
  key: string;
  root: OverviewEntry;
  signals: OverviewEntry[];
}

function getArrivalTime(entry: OverviewEntry): number {
  return new Date(entry.creationTime ?? entry.startTime).getTime();
}

function groupCalendarEntries(entries: OverviewEntry[]): CalendarEntryGroup[] {
  const coworkByItemId = new Map<string, OverviewEntry[]>();
  const groups: CalendarEntryGroup[] = [];

  for (const entry of entries) {
    if (entry.appName !== 'CopilotCowork') {
      groups.push({
        key: getEntryKey(entry),
        root: entry,
        signals: [entry],
      });
      continue;
    }
    const existing = coworkByItemId.get(entry.itemId);
    if (existing) {
      existing.push(entry);
    } else {
      coworkByItemId.set(entry.itemId, [entry]);
    }
  }

  for (const [itemId, signals] of coworkByItemId) {
    signals.sort((a, b) => getArrivalTime(a) - getArrivalTime(b));
    const root = signals[0];
    if (!root) continue;
    groups.push({ key: `cowork:${itemId}`, root, signals });
  }

  return groups.sort(
    (a, b) =>
      new Date(a.root.startTime).getTime() -
      new Date(b.root.startTime).getTime(),
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OverviewView({
  activeTab,
  onTabChange,
  overviewEntries,
  overviewLoading,
  overviewError,
  sortedOverviewEntries,
  recentSortBy,
  recentSortDir,
  onRecentSort,
  timelineEntries,
  timelineLoading,
  timelineError,
  calendarFilterAgents,
  calendarAgentNames,
  calendarFilterPlatforms,
  calendarPlatformNames,
  filteredTimelineEntries,
  timelineDays,
  onCalendarFilterChange,
  onCalendarPlatformFilterChange,
  onRefreshTimeline,
  cvInput,
  onCvInputChange,
  fetchLoading,
  loadJsonInput,
  loadJsonError,
  onLoadJsonChange,
  onLoadJson,
  loadJsonMode,
  onLoadJsonModeChange,
  onSelect,
}: OverviewViewProps) {
  const styles = useStyles();

  const [colWidths, setColWidths] = useState({
    lastViewed: 80,
    interactionDate: 140,
    cv: 160,
  });
  const resizingRef = useRef<{
    col: keyof typeof colWidths;
    startX: number;
    startWidth: number;
  } | null>(null);
  const [expandedCoworkItems, setExpandedCoworkItems] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      const { col, startX, startWidth } = resizingRef.current;
      setColWidths((prev) => ({
        ...prev,
        [col]: Math.max(40, startWidth + (e.clientX - startX)),
      }));
    };
    const onMouseUp = () => {
      resizingRef.current = null;
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startResize = (col: keyof typeof colWidths, e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = {
      col,
      startX: e.clientX,
      startWidth: colWidths[col],
    };
  };

  return (
    <>
      {/* ---- Tab row ---- */}
      <div className={styles.tabRow}>
        <TabList
          className={styles.tabs}
          selectedValue={activeTab}
          onTabSelect={(_, data) => onTabChange(data.value as typeof activeTab)}
        >
          <Tab value="timeline">Agent Interactions</Tab>
          <Tab value="recent">Recently Viewed</Tab>
          <Tab value="search">Search for Interaction</Tab>
          <Tab value="load">Load from JSON</Tab>
        </TabList>

        {activeTab === 'timeline' && (
          <div className={styles.tabActions}>
            <Button
              appearance="subtle"
              icon={<ArrowClockwise24Regular />}
              size="small"
              onClick={onRefreshTimeline}
            >
              Refresh
            </Button>
            <Popover positioning="below-end">
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  appearance={
                    calendarFilterAgents.length > 0 ||
                    calendarFilterPlatforms.length > 0
                      ? 'primary'
                      : 'subtle'
                  }
                  icon={<Filter24Regular />}
                  size="small"
                >
                  {calendarFilterAgents.length > 0 ||
                  calendarFilterPlatforms.length > 0
                    ? `${calendarFilterAgents.length + calendarFilterPlatforms.length} filter${calendarFilterAgents.length + calendarFilterPlatforms.length !== 1 ? 's' : ''}`
                    : 'Filter'}
                </Button>
              </PopoverTrigger>
              <PopoverSurface>
                <div className={styles.calendarFilterPopover}>
                  <span className={styles.calendarFilterLabel}>Platform</span>
                  {calendarPlatformNames.map((name) => (
                    <Checkbox
                      key={name}
                      label={name}
                      checked={calendarFilterPlatforms.includes(name)}
                      onChange={(_, d) => {
                        onCalendarPlatformFilterChange(
                          d.checked
                            ? [...calendarFilterPlatforms, name]
                            : calendarFilterPlatforms.filter((n) => n !== name),
                        );
                      }}
                    />
                  ))}
                  <span className={styles.calendarFilterLabel}>Agent</span>
                  {calendarAgentNames.map((name) => (
                    <Checkbox
                      key={name}
                      label={name}
                      checked={calendarFilterAgents.includes(name)}
                      onChange={(_, d) => {
                        onCalendarFilterChange(
                          d.checked
                            ? [...calendarFilterAgents, name]
                            : calendarFilterAgents.filter((n) => n !== name),
                        );
                      }}
                    />
                  ))}
                  {(calendarFilterAgents.length > 0 ||
                    calendarFilterPlatforms.length > 0) && (
                    <Button
                      appearance="subtle"
                      size="small"
                      onClick={() => {
                        onCalendarFilterChange([]);
                        onCalendarPlatformFilterChange([]);
                      }}
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              </PopoverSurface>
            </Popover>
          </div>
        )}
      </div>

      {/* ---- Recently Viewed tab ---- */}
      {activeTab === 'recent' && (
        <>
          {overviewLoading && (
            <Spinner size="medium" label="Loading signals..." />
          )}

          {overviewError && (
            <MessageBar intent="warning">
              <MessageBarBody>{overviewError}</MessageBarBody>
            </MessageBar>
          )}

          {!overviewLoading &&
            !overviewError &&
            overviewEntries.length === 0 && (
              <div className={styles.emptyState}>
                <p>No recently viewed signals yet.</p>
                <p>
                  Signals appear here when you fetch them via search, drill from
                  the timeline, or deeplink to AI studio.
                </p>
              </div>
            )}

          {!overviewLoading && overviewEntries.length > 0 && (
            <>
              <div className={styles.recentHeader}>
                <span
                  role="button"
                  tabIndex={0}
                  style={{ width: colWidths.lastViewed, flexShrink: 0 }}
                  className={mergeClasses(
                    styles.recentHeaderTime,
                    styles.recentHeaderSortable,
                    recentSortBy === 'lastViewed' &&
                      styles.recentHeaderSortActive,
                  )}
                  onClick={() => onRecentSort('lastViewed')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRecentSort('lastViewed');
                    }
                  }}
                >
                  Last Viewed
                  {recentSortBy === 'lastViewed'
                    ? recentSortDir === 'desc'
                      ? ' ↓'
                      : ' ↑'
                    : ''}
                </span>
                <div
                  role="button"
                  aria-label="Resize Last Viewed column"
                  tabIndex={0}
                  className={styles.resizeHandle}
                  onMouseDown={(e) => startResize('lastViewed', e)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft')
                      setColWidths((p) => ({
                        ...p,
                        lastViewed: Math.max(40, p.lastViewed - 8),
                      }));
                    if (e.key === 'ArrowRight')
                      setColWidths((p) => ({
                        ...p,
                        lastViewed: p.lastViewed + 8,
                      }));
                  }}
                />
                <span
                  role="button"
                  tabIndex={0}
                  style={{ width: colWidths.interactionDate, flexShrink: 0 }}
                  className={mergeClasses(
                    styles.recentHeaderCreated,
                    styles.recentHeaderSortable,
                    recentSortBy === 'interactionDate' &&
                      styles.recentHeaderSortActive,
                  )}
                  onClick={() => onRecentSort('interactionDate')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRecentSort('interactionDate');
                    }
                  }}
                >
                  Interaction Date
                  {recentSortBy === 'interactionDate'
                    ? recentSortDir === 'desc'
                      ? ' ↓'
                      : ' ↑'
                    : ''}
                </span>
                <div
                  role="button"
                  aria-label="Resize Interaction Date column"
                  tabIndex={0}
                  className={styles.resizeHandle}
                  onMouseDown={(e) => startResize('interactionDate', e)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft')
                      setColWidths((p) => ({
                        ...p,
                        interactionDate: Math.max(40, p.interactionDate - 8),
                      }));
                    if (e.key === 'ArrowRight')
                      setColWidths((p) => ({
                        ...p,
                        interactionDate: p.interactionDate + 8,
                      }));
                  }}
                />
                <span
                  style={{ width: colWidths.cv, flexShrink: 0 }}
                  className={styles.recentHeaderCv}
                >
                  CV
                </span>
                <div
                  role="button"
                  aria-label="Resize CV column"
                  tabIndex={0}
                  className={styles.resizeHandle}
                  onMouseDown={(e) => startResize('cv', e)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft')
                      setColWidths((p) => ({
                        ...p,
                        cv: Math.max(40, p.cv - 8),
                      }));
                    if (e.key === 'ArrowRight')
                      setColWidths((p) => ({ ...p, cv: p.cv + 8 }));
                  }}
                />
                <span className={styles.recentHeaderPrompt}>
                  Interaction Summary
                </span>
              </div>
              <div className={styles.recentEntries}>
                {sortedOverviewEntries.map((entry) => (
                  <div
                    key={entry.cv}
                    className={styles.recentEntry}
                    onClick={() => onSelect(entry.cv)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        onSelect(entry.cv);
                    }}
                  >
                    <span
                      className={styles.recentEntryTime}
                      style={{ width: colWidths.lastViewed, flexShrink: 0 }}
                    >
                      {new Date(entry.timestamp).toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </span>
                    <div className={styles.colDivider} />
                    {entry.startTime && (
                      <span
                        className={styles.recentEntryCreated}
                        style={{
                          width: colWidths.interactionDate,
                          flexShrink: 0,
                        }}
                      >
                        {new Date(entry.startTime).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    )}
                    <div className={styles.colDivider} />
                    <span
                      className={styles.recentEntryCv}
                      style={{ width: colWidths.cv, flexShrink: 0 }}
                    >
                      {entry.cv}
                    </span>
                    <div className={styles.colDivider} />
                    {entry.promptPreview && (
                      <span className={styles.recentEntryPrompt}>
                        {entry.promptPreview}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ---- Search tab ---- */}
      {activeTab === 'search' && (
        <div className={styles.searchTab}>
          <div className={styles.searchRow}>
            <Input
              className={styles.searchInput}
              placeholder="e.g. AbCdEf123456.1.2"
              value={cvInput}
              onChange={(_, d) => onCvInputChange(d.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !fetchLoading && cvInput.trim()) {
                  onSelect(cvInput.trim());
                }
              }}
              appearance="outline"
            />
            <Button
              appearance="primary"
              icon={fetchLoading ? <Spinner size="tiny" /> : <FetchIcon />}
              onClick={() => {
                if (cvInput.trim()) onSelect(cvInput.trim());
              }}
              disabled={fetchLoading || !cvInput.trim()}
            >
              {fetchLoading ? 'Fetching...' : 'View Signal'}
            </Button>
          </div>
          <Text className={styles.searchHint}>
            Enter a CV or ItemId to load the full signal details.
          </Text>
        </div>
      )}

      {/* ---- Load from JSON tab ---- */}
      {activeTab === 'load' && (
        <div className={styles.ocvTab}>
          <RadioGroup
            layout="horizontal"
            aria-label="Payload type"
            value={loadJsonMode}
            onChange={(_, d) => onLoadJsonModeChange(d.value as LoadJsonMode)}
          >
            <Radio value="signal" label="Signal (SIGS / OCV)" />
            <Radio value="trajectory" label="Execution trajectory" />
          </RadioGroup>
          <Textarea
            className={styles.ocvTextarea}
            textarea={{ className: styles.ocvTextareaInner }}
            placeholder={
              loadJsonMode === 'trajectory'
                ? 'Paste ExecutionTrajectory JSON here...'
                : 'Paste JSON payload here...'
            }
            value={loadJsonInput}
            onChange={(_, d) => onLoadJsonChange(d.value)}
            appearance="outline"
          />
          {loadJsonError && (
            <MessageBar intent="error">
              <MessageBarBody>{loadJsonError}</MessageBarBody>
            </MessageBar>
          )}
          <div className={styles.ocvActions}>
            <Button
              appearance="primary"
              onClick={() => onLoadJson(loadJsonInput)}
              disabled={!loadJsonInput.trim()}
            >
              Load
            </Button>
          </div>
          <Text className={styles.ocvHint}>
            {loadJsonMode === 'trajectory'
              ? 'Paste an ExecutionTrajectory JSON (schemaVersion + messages) to view only its Trajectory timeline.'
              : 'Paste a raw SIGS signal JSON or OCV JSON payload to load it directly without fetching from Substrate.'}
          </Text>
        </div>
      )}

      {/* ---- LLM Queries / Timeline tab ---- */}
      {activeTab === 'timeline' && (
        <div className={styles.calendarWrapper}>
          {timelineError === 'no-token' && (
            <div className={styles.emptyState}>
              <p>
                A bearer token is required to debug.&nbsp;
                <a
                  href="https://domoreexp.visualstudio.com/Teamspace/_git/ai-studio?path=/docs/content/feedback-debuggability/debugging-guide.md&version=GBmaster&_a=preview&anchor=token-management"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  How to
                </a>
              </p>
              <p>
                Click the <strong>⚙</strong> settings icon above to set your
                bearer token.
              </p>
            </div>
          )}

          {timelineError === 'token-expired' && (
            <MessageBar intent="error">
              <MessageBarBody>
                Bearer token expired. Update it via the settings icon ⚙ and
                click Refresh.
              </MessageBarBody>
            </MessageBar>
          )}

          {timelineError &&
            timelineError !== 'no-token' &&
            timelineError !== 'token-expired' && (
              <MessageBar intent="error">
                <MessageBarBody>{timelineError}</MessageBarBody>
              </MessageBar>
            )}

          {timelineLoading && (
            <Spinner size="medium" label="Fetching signals from Substrate..." />
          )}

          {!timelineLoading && !timelineError && (
            <div className={styles.calendarOuter}>
              <div className={styles.calendarInner}>
                {buildCalendarDays(
                  groupCalendarEntries(filteredTimelineEntries),
                  (group) => group.root.startTime,
                  timelineDays,
                ).map(({ label, dayStart, entries: dayGroups }) => (
                  <div key={dayStart} className={styles.calendarDayCol}>
                    <div className={styles.calendarDayHeader}>{label}</div>
                    <div className={styles.calendarDayEntries}>
                      {dayGroups.map((group) => {
                        const entry = group.root;
                        const isAugloop = entry.appName === 'AugmentationLoop';
                        const isSydney = entry.appName?.includes('Sydney');
                        const isCowork = entry.appName === 'CopilotCowork';
                        const signalIdentifier = getSignalIdentifier(entry);
                        const isCoworkGroup = group.signals.length > 1;
                        const isExpanded = expandedCoworkItems.has(group.key);
                        if (isCoworkGroup) {
                          const toggle = () =>
                            setExpandedCoworkItems((current) => {
                              const next = new Set(current);
                              if (next.has(group.key)) next.delete(group.key);
                              else next.add(group.key);
                              return next;
                            });
                          return (
                            <div
                              key={group.key}
                              className={styles.calendarGroup}
                            >
                              <div
                                className={mergeClasses(
                                  styles.calendarGroupHeader,
                                  isExpanded &&
                                    styles.calendarGroupHeaderExpanded,
                                )}
                                onClick={toggle}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isExpanded}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                  ) {
                                    event.preventDefault();
                                    toggle();
                                  }
                                }}
                              >
                                <div className={styles.calendarGroupHeaderRow}>
                                  <Tooltip
                                    content={
                                      entry.promptPreview ??
                                      entry.agentName ??
                                      entry.itemId
                                    }
                                    relationship="description"
                                  >
                                    <span
                                      className={styles.calendarEntryPrompt}
                                    >
                                      {entry.promptPreview ??
                                        entry.agentName ??
                                        entry.itemId}
                                    </span>
                                  </Tooltip>
                                  <span className={styles.calendarGroupCount}>
                                    {group.signals.length} signals
                                  </span>
                                  {isExpanded ? (
                                    <ChevronDown16Regular
                                      className={styles.calendarGroupChevron}
                                    />
                                  ) : (
                                    <ChevronRight16Regular
                                      className={styles.calendarGroupChevron}
                                    />
                                  )}
                                </div>
                                <div className={styles.calendarEntryMeta}>
                                  <span className={styles.calendarEntryTime}>
                                    {new Date(
                                      entry.startTime,
                                    ).toLocaleTimeString(undefined, {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                  <span
                                    className={mergeClasses(
                                      styles.calendarEntryBadge,
                                      styles.calendarEntryBadgeCowork,
                                    )}
                                  >
                                    CW
                                  </span>
                                </div>
                              </div>
                              {isExpanded && (
                                <div className={styles.calendarGroupSigs}>
                                  {group.signals.map((signal) => {
                                    const identifier =
                                      getSignalIdentifier(signal);
                                    return (
                                      <a
                                        key={
                                          signal.signalId
                                            ? signal.signalId
                                            : `${signal.cv}\u0000${signal.startTime}`
                                        }
                                        className={styles.calendarGroupSig}
                                        href={debugHref(identifier)}
                                        onClick={(event) => {
                                          event.preventDefault();
                                          onSelect(identifier);
                                        }}
                                      >
                                        <Tooltip
                                          content={
                                            signal.promptPreview ??
                                            signal.agentName ??
                                            signal.cv
                                          }
                                          relationship="description"
                                        >
                                          <span
                                            className={
                                              styles.calendarGroupSigPrompt
                                            }
                                          >
                                            {signal.promptPreview ??
                                              signal.agentName ??
                                              signal.cv}
                                          </span>
                                        </Tooltip>
                                        <div
                                          className={
                                            styles.calendarGroupSigMeta
                                          }
                                        >
                                          <span
                                            className={styles.calendarEntryTime}
                                          >
                                            {new Date(
                                              signal.startTime,
                                            ).toLocaleTimeString(undefined, {
                                              hour: '2-digit',
                                              minute: '2-digit',
                                            })}
                                          </span>
                                          <Tooltip
                                            content={signal.cv}
                                            relationship="description"
                                          >
                                            <span
                                              className={
                                                styles.calendarGroupSigId
                                              }
                                            >
                                              {abbreviateIdentifier(signal.cv)}
                                            </span>
                                          </Tooltip>
                                        </div>
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <a
                            key={group.key}
                            className={styles.calendarEntry}
                            href={debugHref(signalIdentifier)}
                            onClick={(e) => {
                              e.preventDefault();
                              onSelect(signalIdentifier);
                            }}
                          >
                            <Tooltip
                              content={
                                entry.promptPreview ??
                                entry.agentName ??
                                entry.itemId
                              }
                              relationship="description"
                            >
                              <span className={styles.calendarEntryPrompt}>
                                {entry.promptPreview ??
                                  entry.agentName ??
                                  entry.itemId}
                              </span>
                            </Tooltip>
                            {entry.promptPreview && entry.agentName && (
                              <span className={styles.calendarEntryApp}>
                                {entry.agentName}
                              </span>
                            )}
                            <div className={styles.calendarEntryMeta}>
                              <span className={styles.calendarEntryTime}>
                                {new Date(entry.startTime).toLocaleTimeString(
                                  undefined,
                                  { hour: '2-digit', minute: '2-digit' },
                                )}
                              </span>
                              {isAugloop && (
                                <span
                                  className={mergeClasses(
                                    styles.calendarEntryBadge,
                                    styles.calendarEntryBadgeAugloop,
                                  )}
                                >
                                  AL
                                </span>
                              )}
                              {isSydney && (
                                <span
                                  className={mergeClasses(
                                    styles.calendarEntryBadge,
                                    styles.calendarEntryBadgeSydney,
                                  )}
                                >
                                  SY
                                </span>
                              )}
                              {isCowork && (
                                <span
                                  className={mergeClasses(
                                    styles.calendarEntryBadge,
                                    styles.calendarEntryBadgeCowork,
                                  )}
                                >
                                  CW
                                </span>
                              )}
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!timelineLoading && !timelineError && (
            <div className={styles.calendarFooter}>
              <Text
                size={200}
                style={{ color: tokens.colorNeutralForeground3 }}
              >
                {calendarFilterAgents.length > 0
                  ? `${filteredTimelineEntries.length} of ${timelineEntries.length}`
                  : timelineEntries.length}{' '}
                interaction{timelineEntries.length !== 1 ? 's' : ''} · last 7
                days
              </Text>
            </div>
          )}
        </div>
      )}
    </>
  );
}
